import React, { useEffect, useState } from 'react';
import PageSkeleton from '../common/Skeleton';
import Pagination from '../common/Pagination';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Trash2, Plus } from 'lucide-react';
import { IconButton, Button, PageHeader, AlertMessage, DataTable, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell, EmptyState, SearchFilterBar } from '../common/ui';
import { getUserPreference, setUserPreference } from '../../hooks/useUserPreferences';

const DeviceTypesList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(() => parseInt(getUserPreference('deviceTypes', 'itemsPerPage', '50')));
  const [searchTerm, setSearchTerm] = useState(() => {
    return searchParams.get('search') || '';
  });

  const load = async (page = 1, limit = itemsPerPage, search = searchTerm) => {
    try {
      setError('');
      setLoading(true);
      
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', limit.toString());
      if (search && search.trim()) {
        params.set('search', search.trim());
      }
      
      const res = await axios.get(`/api/products?${params.toString()}`);
      
      // Handle paginated response
      const productsData = res.data.products || res.data;
      
      if (Array.isArray(productsData)) {
        setItems(productsData);
        
        // Handle pagination metadata
        if (res.data.pagination) {
          setTotalPages(res.data.pagination.totalPages || 1);
          setTotalItems(res.data.pagination.totalItems || productsData.length);
          setCurrentPage(res.data.pagination.currentPage || page);
        } else {
          // Fallback for backends that don't return pagination metadata
          setTotalPages(productsData.length === limit ? page + 1 : page);
          setTotalItems(productsData.length);
          setCurrentPage(page);
        }
      } else {
        setItems(productsData);
        // For non-paginated response, set defaults
        setTotalPages(1);
        setTotalItems(productsData.length || 0);
        setCurrentPage(1);
      }
    } catch (e) {
      setError('Failed to load device types');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    // Initialize from URL params
    const searchFromUrl = searchParams.get('search') || '';
    load(1, itemsPerPage, searchFromUrl);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen to URL parameter changes (for browser back/forward)
  useEffect(() => {
    const searchFromUrl = searchParams.get('search') || '';
    
    // Only update if values actually changed
    if (searchFromUrl !== searchTerm) {
      setSearchTerm(searchFromUrl);
      setCurrentPage(1);
      load(1, itemsPerPage, searchFromUrl);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const runSearch = async () => {
    setCurrentPage(1);
    // Update URL params
    updateUrlParams(searchTerm);
    load(1, itemsPerPage, searchTerm);
  };

  // Helper function to update URL parameters
  const updateUrlParams = (search) => {
    const newParams = new URLSearchParams();
    
    // Only add non-default values to keep URLs clean
    if (search && search.trim()) {
      newParams.set('search', search.trim());
    }
    
    setSearchParams(newParams);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this device type?')) return;
    try {
      await axios.delete(`/api/products/${id}`);
      load(currentPage);
    } catch (e) {
      setError('Failed to delete');
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setCurrentPage(newPage);
      load(newPage);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setUserPreference('deviceTypes', 'itemsPerPage', newItemsPerPage);
    setCurrentPage(1);
    load(1, newItemsPerPage);
  };

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Products</h1>
        <PageSkeleton rows={3} rowHeight="h-16" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <PageHeader
        title="Products"
        subtitle="Manage device types and their configurations"
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => navigate('/devices')}
            >
              Back to Devices
            </Button>
            <Button
              variant="primary"
              icon={Plus}
              onClick={async () => {
                const name = window.prompt('Enter new product name');
                if (!name || !name.trim()) return;
                try {
                  const res = await axios.post('/api/products', { name: name.trim() });
                  navigate(`/catalog/products/${res.data.id}/edit`);
                } catch (e) {
                  setError('Failed to create product');
                }
              }}
            >
              Add Product
            </Button>
          </>
        }
      />

      <SearchFilterBar
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        onSearch={runSearch}
        onSearchSubmit={runSearch}
        searchPlaceholder="Search products..."
        useProductAutocomplete={true}
      />

      {error && <AlertMessage type="error" message={error} />}

      <DataTable>
        <TableHeader>
          <TableHeaderCell>Name</TableHeaderCell>
          <TableHeaderCell>Attributes</TableHeaderCell>
          <TableHeaderCell>Modules</TableHeaderCell>
          <TableHeaderCell align="center">Actions</TableHeaderCell>
        </TableHeader>
        <TableBody>
          {items.length === 0 ? (
            <EmptyState
              title={searchTerm ? "No products found" : "No products found"}
              description={searchTerm ? `No products match "${searchTerm}". Try a different search term.` : "Get started by creating your first product."}
            />
          ) : (
            items.map((it) => (
              <TableRow
                key={it.id}
                clickable={true}
                href={`/catalog/products/${it.id}/edit`}
                onClick={() => navigate(`/catalog/products/${it.id}/edit`)}
              >
                <TableCell className="font-medium">{it.name}</TableCell>
                <TableCell className="text-gray-500">
                  {it.attribute_count || 0} attribute{(it.attribute_count || 0) !== 1 ? 's' : ''}
                </TableCell>
                <TableCell className="text-gray-500">
                  {it.module_count || 0} module{(it.module_count || 0) !== 1 ? 's' : ''}
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    icon={Trash2}
                    variant="delete"
                    size="sm"
                    title="Delete product"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(it.id);
                    }}
                  />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </DataTable>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />
    </div>
  );
};

export default DeviceTypesList;


